import { ApiError, BaseSuccessResponse, ServerError } from '@/types/commonTypes';
import { AxiosResponse } from 'axios';

export default {
  /**
   * Checks if the response status is OK (2xx)
   * @param response - The Axios response object to check.
   * @returns True if the response status is OK, false otherwise.
   */
  isStatusOk(response: AxiosResponse): boolean {
    return response.status >= 200 && response.status < 300;
  },
  isSuccess(response: AxiosResponse): response is AxiosResponse<BaseSuccessResponse> {
    const data = response.data;
    return (
      this.isStatusOk(response) &&
      typeof data === 'object' &&
      data !== null &&
      'isSuccess' in data &&
      typeof (data as BaseSuccessResponse).isSuccess === 'boolean'
    );
  },
  /**
   * Checks if the response status indicates an error (4xx or 5xx).
   * @param response - The Axios response object to check.
   * @returns True if the response status is an error, false otherwise.
   */
  isStatusError(response: AxiosResponse): boolean {
    return response.status >= 400 && response.status < 600;
  },

  /**
   * Checks if the response indicates an API error.
   * @param response - The Axios response object to check.
   * @returns True if the response is an API error, false otherwise.
   * @remarks Checks if the response data has an `errors` property.
   */
  isApiError(response: AxiosResponse): response is AxiosResponse<ApiError> {
    const data = response.data;
    return (
      this.isStatusError(response) &&
      typeof data === 'object' &&
      data !== null &&
      'errors' in data &&
      typeof (data as ApiError).type === 'string' &&
      typeof (data as ApiError).status === 'number'
    );
  },
  /**
   * Checks if the response indicates a server failure.
   * @param response - The Axios response object to check.
   * @returns True if the response is a server failure, false otherwise.
   * @remarks Checks if the response data has a title containing an error message, and no `errors` property in its data.
   */
  isServerFailure(response: AxiosResponse): response is AxiosResponse<ServerError> {
    const data = response.data;
    return (
      this.isStatusError(response) &&
      typeof data === 'object' &&
      data !== null &&
      'traceId' in data &&
      !('errors' in data) &&
      typeof (data as ServerError).title === 'string'
    );
  },
  getErrorMessages(response: AxiosResponse): string[] {
    if (this.isApiError(response)) {
      const errorData = response.data.errors;
      return errorData['error'] ?? Object.values(errorData).flat();
    } else if (this.isServerFailure(response)) {
      return [response.data.title];
    } else {
      return ['An unexpected error occurred'];
    }
  },
};
